import { WalletAdapter, walletError } from '../types'
import { resolveApiUrl } from '../../utils'
import { POST } from 'rest/request'

/**
 * Apple Pay w Safari/SFSafariViewController, wariant "domeny zarządzane przez operatora" (PayU):
 * sesję merchanta pobiera nasz backend (validationUrl), bez CSR/certyfikatów po naszej stronie web.
 * Warunek działania: Safari + domena strony płatności zwalidowana w panelu operatora.
 *
 * UWAGA: `applePay.merchantIdentifier` na tym samym walletForm służy WYŁĄCZNIE natywnej appce
 * iOS (nativeApplePay.ts, PKPaymentRequest) - web go nie używa i NIE wolno go przekazywać do
 * `canMakePaymentsWithActiveCard()`, bo dałoby to fałszywe "brak karty" w Safari (§1.4 kontraktu).
 * Natywna appka i tak nigdy nie trafia do tego adaptera - patrz adapters/wallet/index.ts.
 */

/** Apple oczekuje dokładnie takich nazw sieci - 'mastercard' małymi literami NIE zadziała */
const APPLE_NETWORKS: Record<string, string> = {
  VISA: 'visa',
  MASTERCARD: 'masterCard',
  MAESTRO: 'maestro',
  AMEX: 'amex',
  DISCOVER: 'discover',
  JCB: 'jcb'
}

const toAppleNetworks = (networks: string[] = []) =>
  networks.map(network => APPLE_NETWORKS[network.toUpperCase()] || network)

export const applePayAdapter: WalletAdapter = {
  async isAvailable(walletForm) {
    const config = walletForm?.applePay
    if (!config?.validationUrl) return false

    // na dokumencie bez HTTPS canMakePayments() rzuca InvalidAccessError zamiast zwrócić false
    if (window.location.protocol !== 'https:') return false

    const Session = window.ApplePaySession
    if (!Session || typeof Session.canMakePayments !== 'function') return false
    try {
      // Tylko canMakePayments() - config.merchantIdentifier jest dla appki natywnej (PKPaymentRequest),
      // nie dla Safari; użycie go w canMakePaymentsWithActiveCard() dałoby tu fałszywe "brak karty".
      return Session.canMakePayments()
    } catch (_) {
      return false
    }
  },

  requestToken(walletForm, { amount, label, posId, onAuthorized }) {
    return new Promise<string>((resolve, reject) => {
      const config = walletForm.applePay
      const validationUrl = resolveApiUrl(config?.validationUrl)
      if (!validationUrl) {
        reject(walletError('wallet_unavailable'))
        return
      }
      if (!onAuthorized) {
        reject(walletError('wallet_authorize_handler_missing'))
        return
      }

      const Session = window.ApplePaySession
      const session = new Session(3, {
        countryCode: walletForm.countryCode,
        currencyCode: walletForm.currencyCode,
        supportedNetworks: toAppleNetworks(walletForm.supportedNetworks),
        // lista z kontraktu, gdy operator ją poda; domyślnie 3DS
        merchantCapabilities: config?.merchantCapabilities?.length
          ? config.merchantCapabilities
          : ['supports3DS'],
        total: {
          label: config?.displayName || label,
          amount: amount.toFixed(2)
        }
      })

      session.onvalidatemerchant = async () => {
        try {
          const merchantSession = await POST(validationUrl, {
            body: {
              posId,
              domain: window.location.hostname,
              displayName: config?.displayName
            }
          })
          session.completeMerchantValidation(merchantSession)

        } catch (error) {
          try {
            session.abort()
          } catch (_) { }
          reject(walletError('wallet_merchant_validation_failed'))
        }
      }

      session.onpaymentauthorized = async (event: any) => {
        const token = JSON.stringify(event?.payment?.token?.paymentData)
        try {
          // UWAGA: tu leci samo rozliczenie, BEZ odpytywania o status -
          // arkusz Apple nie doczekałby pollingu. Status sprawdzamy po jego zamknięciu.
          const accepted = await onAuthorized(token)
          session.completePayment(accepted ? Session.STATUS_SUCCESS : Session.STATUS_FAILURE)
          if (accepted) resolve(token)
          else reject(walletError('wallet_settlement_failed'))

        } catch (error) {
          session.completePayment(Session.STATUS_FAILURE)
          reject(walletError('wallet_settlement_failed'))
        }
      }

      session.oncancel = () => reject(walletError('wallet_cancelled'))

      session.begin()
    })
  }
}
